import { useState } from "react";
import type { Booking, Customer, CreditTransaction, CreditTransactionBooking, ResourceType } from "../lib/types";
import { RESOURCE_LABELS } from "../lib/types";
import { userCancelBooking, payBooking, redirectToCheckout } from "../lib/hooks";
import { localNow, splitByGroupEnd } from "../lib/bookingSplit";

export type MyBookingsSubTab = "reservations" | "credit";

interface MyBookingsProps {
  bookings: Booking[];
  customer: Customer | null;
  loading: boolean;
  creditBalance: number;
  creditTransactions: CreditTransaction[];
  onCancelled: () => void;
  subTab: MyBookingsSubTab;
  onSubTabChange: (next: MyBookingsSubTab) => void;
  onOpenVoucher: () => void;
}

/** Cancel notice, same rule as the API: 48 h for a workshop, 15 min for anything else. */
const WORKSHOP_CANCEL_NOTICE_MS = 48 * 60 * 60 * 1000;
const SLOT_CANCEL_NOTICE_MS = 15 * 60 * 1000;
const WORKSHOP_CANCEL_TOOLTIP = "Workshop je možné zrušiť najneskôr 48 hodín pred začiatkom.";
const SLOT_CANCEL_TOOLTIP = "Rezerváciu je možné zrušiť najneskôr 15 minút pred začiatkom.";

/** Returns why the group can no longer be cancelled, or null when it still can. */
function cancelLockReason(group: BookingGroup): string | null {
  const isWorkshop = group.resourceType === "workshop";
  const notice = isWorkshop ? WORKSHOP_CANCEL_NOTICE_MS : SLOT_CANCEL_NOTICE_MS;
  const start = new Date(`${group.date}T${group.startTime}`);
  if (start.getTime() - Date.now() >= notice) return null;
  return isWorkshop ? WORKSHOP_CANCEL_TOOLTIP : SLOT_CANCEL_TOOLTIP;
}

/** "15" for whole euros, "13,50" once a discount leaves cents. */
function formatBookingPrice(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2).replace(".", ",");
}

interface BookingGroup {
  groupId: string;
  bookings: Booking[];
  date: string;
  startTime: string;
  endTime: string;
  resourceType: ResourceType;
  label: string;
  numPersons: number;
  totalPriceCents: number;
  /** Before the discount (slot price × persons); the difference is what the code took off. */
  listPriceCents: number;
  discountCode?: string;
  status: "confirmed" | "pending_payment";
  checkoutUrl?: string;
}

function groupBookings(bookings: Booking[]): BookingGroup[] {
  const groupMap = new Map<string, Booking[]>();

  for (const b of bookings) {
    if (!b.slots || !b.booking_group_id) continue;
    const key = b.booking_group_id;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(b);
  }

  const groups: BookingGroup[] = [];
  for (const [groupId, items] of groupMap) {
    // Sort by start_time
    items.sort((a, b) => a.slots!.start_time.localeCompare(b.slots!.start_time));
    const first = items[0];
    const last = items[items.length - 1];
    if (!first.slots || !last.slots) continue;

    const resType = first.slots.resource_type as ResourceType;
    const numPersons = first.num_persons ?? 1;
    const totalPriceCents = items.reduce((sum, b) => sum + b.booked_price_cents, 0);
    const listPriceCents = items.reduce((sum, b) => sum + b.slots!.price_cents * (b.num_persons ?? 1), 0);

    groups.push({
      groupId,
      bookings: items,
      date: first.slots.date,
      startTime: first.slots.start_time,
      endTime: last.slots.end_time,
      resourceType: resType,
      label: first.slots.title || RESOURCE_LABELS[resType] || first.slots.resource_type,
      numPersons,
      totalPriceCents,
      listPriceCents,
      discountCode: first.discount_code,
      status: first.status === "pending_payment" ? "pending_payment" : "confirmed",
      checkoutUrl: first.checkout_url,
    });
  }

  // Sort by date then start time
  groups.sort((a, b) => {
    const cmp = a.date.localeCompare(b.date);
    return cmp !== 0 ? cmp : a.startTime.localeCompare(b.startTime);
  });

  return groups;
}

// Stĺpec Typ v zozname rezervácií (variant B od dizajnérky: Stôl / Kruh / Workshop).
const KIND_LABELS: Record<ResourceType, string> = {
  stol: "Stôl",
  kruh: "Kruh",
  workshop: "Workshop",
};

// Záložka Kredit obsahuje len pohyby interného kreditu (nie platby kartou),
// preto názvy hovoria o použití / vrátení kreditu, nie o rezervácii samotnej.
const TRANSACTION_LABELS: Record<string, string> = {
  cancellation_refund: "Vrátenie za storno",
  booking_deduction: "Platba kreditom",
  admin_adjustment: "Úprava adminom",
  payment_refund: "Vrátenie, platba neprebehla",
  voucher_redemption: "Darčekový poukaz",
};

// Popisy z backendu začínajú starým názvom typu („Rezervácia (…)“, „Storno rezervácie (…)“),
// ten pri zobrazení odstránime a ukážeme len detail v zátvorke.
const DESCRIPTION_PREFIXES = ["Storno rezervácie", "Rezervácia", "Admin storno", "Vrátenie kreditu", "Doplatok rezervácie z kreditu", "Darčekový poukaz"];

/** „Stôl · 9. 10. · 12:00–14:00 · 3 os.“ z prepojenej rezervácie; rovnaké krátke typy ako stĺpec Typ. */
function describeBooking(b: CreditTransactionBooking): string {
  const date = new Date(b.date + "T00:00:00");
  const name = b.title || KIND_LABELS[b.resource_type] || b.resource_type;
  const parts = [name, `${date.getDate()}. ${date.getMonth() + 1}.`, `${b.start_time}–${b.end_time}`];
  if (b.num_persons > 1) parts.push(`${b.num_persons} os.`);
  return parts.join(" · ");
}

/** Záložný popis z uloženého textu, keď transakcia nemá prepojenú rezerváciu. */
function describeFromText(tx: CreditTransaction): string {
  if (tx.type === "payment_refund") return ""; // názov typu už hovorí, že platba neprebehla
  let detail = (tx.description ?? "").trim();
  for (const prefix of DESCRIPTION_PREFIXES) {
    if (detail.startsWith(prefix)) { detail = detail.slice(prefix.length).trim(); break; }
  }
  return detail.replace(/^\((.*)\)$/, "$1").replace(/^[—–-]\s*/, "");
}

/** The discount code next to the booking's name, always visible: a title tooltip never showed in the
 * embedded widget and does not exist on mobile (QA 175, 2026-10-24). */
function DiscountNote({ code, offCents }: { code?: string; offCents: number }) {
  // A code with nothing taken off is left out: after the admin cancels the discounted first
  // hour of a fixed-amount booking, the remaining hours keep the code but cost full price.
  if (!code || offCents <= 0) return null;
  return <span className="booking-discount">{code} −{formatBookingPrice(offCents)} €</span>;
}

export function MyBookings({ bookings, customer, loading, creditBalance, creditTransactions, onCancelled, subTab, onSubTabChange, onOpenVoucher }: MyBookingsProps) {
  const [cancellingGroup, setCancellingGroup] = useState<string | null>(null);
  const [confirmGroup, setConfirmGroup] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState<{ groupId: string; creditCents: number } | null>(null);
  const [payingGroup, setPayingGroup] = useState<string | null>(null);
  const [paySuccess, setPaySuccess] = useState<{ groupId: string; creditCents: number } | null>(null);
  const [payError, setPayError] = useState<{ groupId: string; message: string } | null>(null);
  const [cancelError, setCancelError] = useState<{ groupId: string; message: string } | null>(null);

  if (loading) {
    return <div className="loading">Načítavam rezervácie...</div>;
  }

  // By end time, not date: an hour that ended this morning is history (lib/bookingSplit).
  const { upcoming: upcomingBookings, past: pastBookings } = splitByGroupEnd(bookings, localNow());

  const upcomingGroups = groupBookings(upcomingBookings);
  const pastIndividual = pastBookings.sort((a, b) => {
    if (!a.slots || !b.slots) return 0;
    const cmp = b.slots.date.localeCompare(a.slots.date);
    return cmp !== 0 ? cmp : b.slots.start_time.localeCompare(a.slots.start_time);
  });

  const handleCancelClick = (groupId: string) => {
    setConfirmGroup(groupId);
  };

  const handleCancelConfirm = async (groupId: string) => {
    setCancelLoading(true);
    setCancellingGroup(groupId);
    setCancelError(null);
    const { data, error } = await userCancelBooking(groupId);
    setCancelLoading(false);
    if (error) {
      setCancellingGroup(null);
      setConfirmGroup(null);
      setCancelError({ groupId, message: error.message });
      return;
    }
    const creditCents = typeof data === "object" && data !== null ? (data as any).refund_credit_cents ?? 0 : 0;
    setCancelSuccess({ groupId, creditCents });
    setConfirmGroup(null);
    setCancellingGroup(null);
    setTimeout(() => {
      setCancelSuccess(null);
      onCancelled();
    }, 2000);
  };

  const handlePay = async (groupId: string) => {
    setPayingGroup(groupId);
    setPayError(null);
    const { data, error } = await payBooking(groupId);
    setPayingGroup(null);
    if (error || !data) {
      setPayError({ groupId, message: error?.message ?? "Platba zlyhala." });
      return;
    }
    if (data.status === "confirmed") {
      setPaySuccess({ groupId, creditCents: data.credit_used_cents });
      setTimeout(() => {
        setPaySuccess(null);
        onCancelled();
      }, 2500);
      return;
    }
    if (data.checkout_url) {
      redirectToCheckout(data.checkout_url);
    }
  };

  const handleCancelCancel = () => {
    setConfirmGroup(null);
  };

  const renderGroupCard = (group: BookingGroup) => {
    const date = new Date(group.date + "T00:00:00");
    const weekday = date.toLocaleDateString("sk-SK", { weekday: "short" });
    const dateStr = `${date.getDate()}. ${date.getMonth() + 1}.`;
    const timeStart = group.startTime.slice(0, 5);
    const timeEnd = group.endTime.slice(0, 5);
    const hourCount = group.bookings.length;
    const price = formatBookingPrice(group.totalPriceCents);

    const isCancelling = cancellingGroup === group.groupId;
    const isConfirming = confirmGroup === group.groupId;
    const isPaying = payingGroup === group.groupId;
    const successInfo = cancelSuccess?.groupId === group.groupId ? cancelSuccess : null;
    const paidInfo = paySuccess?.groupId === group.groupId ? paySuccess : null;
    const payErrorInfo = payError?.groupId === group.groupId ? payError : null;
    const cancelErrorInfo = cancelError?.groupId === group.groupId ? cancelError : null;
    const cancelLock = cancelLockReason(group);

    if (paidInfo) {
      return (
        <div key={group.groupId} className="booking-group-card">
          <div className="credit-notification">
            Rezervácia zaplatená z kreditu ({(paidInfo.creditCents / 100).toFixed(2).replace(".", ",")} €) a potvrdená.
          </div>
        </div>
      );
    }

    if (successInfo) {
      return (
        <div key={group.groupId} className="booking-group-card">
          <div className="credit-notification">
            Rezervácia zrušená.{" "}
            {successInfo.creditCents > 0
              ? `Kredit +${(successInfo.creditCents / 100).toFixed(2).replace(".", ",")} € pripísaný`
              : ""}
          </div>
        </div>
      );
    }

    const pinStatus = group.bookings[0]?.pin_status ?? "";
    const showPinPending = group.status === "confirmed" && pinStatus !== "active";

    return (
      <div key={group.groupId} className="booking-group-card">
        <div className="booking-card booking-card-upcoming">
          <span className="booking-weekday">{weekday}</span>
          <span className="booking-date">{dateStr}</span>
          <span className="booking-time-start">{timeStart}</span>
          <span className="booking-time-dash">–</span>
          <span className="booking-time-end">{timeEnd}</span>
          <span className="booking-kind">{KIND_LABELS[group.resourceType] ?? group.resourceType}</span>
          <span className="booking-type">
            <strong>{group.label}</strong>
            {hourCount > 1 && <span className="booking-hours">({hourCount}h)</span>}
            <DiscountNote code={group.discountCode} offCents={group.listPriceCents - group.totalPriceCents} />
            {group.status === "pending_payment" && <span className="pending-badge">Čaká na platbu</span>}
            {showPinPending && <span className="pin-waiting-badge" title="Prístupový kód ešte nie je doručený. Ak váš termín začína skoro, kontaktujte nás.">PIN čaká</span>}
          </span>
          <span className="booking-persons">{group.numPersons > 1 ? `${group.numPersons} os.` : ""}</span>
          <span className="booking-price">{price} €</span>
          {!isConfirming && (
            <span className="booking-actions">
              {group.status === "pending_payment" && (
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => handlePay(group.groupId)}
                  disabled={isPaying}
                >
                  {isPaying ? "Spracúvam..." : "Zaplatiť"}
                </button>
              )}
              {cancelLock ? (
                <span className="booking-cancel-locked" data-tooltip={cancelLock}>
                  <button className="booking-cancel-btn" disabled>Zrušiť</button>
                </span>
              ) : (
                <button
                  className="booking-cancel-btn"
                  onClick={() => handleCancelClick(group.groupId)}
                  disabled={isCancelling || isPaying}
                >
                  Zrušiť
                </button>
              )}
            </span>
          )}
        </div>
        {group.status === "pending_payment" && creditBalance > 0 && !payErrorInfo && (
          <div className="booking-pay-hint">
            Máte kredit {(creditBalance / 100).toFixed(2).replace(".", ",")} €, pri platbe sa použije.
          </div>
        )}
        {payErrorInfo && <div className="booking-pay-error">{payErrorInfo.message}</div>}
        {cancelErrorInfo && <div className="booking-pay-error">{cancelErrorInfo.message}</div>}
        {isConfirming && (
          <div className="cancel-confirm">
            <p>Naozaj chcete zrušiť túto rezerváciu?</p>
            <div className="cancel-confirm-actions">
              <button
                className="btn btn-primary"
                style={{ background: "#d4453a", fontSize: "0.85rem", padding: "4px 12px" }}
                onClick={() => handleCancelConfirm(group.groupId)}
                disabled={cancelLoading}
              >
                {cancelLoading ? "Ruším..." : "Áno, zrušiť"}
              </button>
              <button
                className="btn btn-secondary"
                style={{ fontSize: "0.85rem", padding: "4px 12px" }}
                onClick={handleCancelCancel}
              >
                Nie
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBooking = (b: Booking) => {
    if (!b.slots) return null;
    const date = new Date(b.slots.date + "T00:00:00");
    // DM Sans nemá tabuľkové číslice, preto má každá časť vlastný stĺpec mriežky.
    const weekday = date.toLocaleDateString("sk-SK", { weekday: "short" });
    const dateStr = `${date.getDate()}. ${date.getMonth() + 1}.`;
    const timeStart = b.slots.start_time.slice(0, 5);
    const timeEnd = b.slots.end_time.slice(0, 5);
    const resType = b.slots.resource_type as ResourceType;
    const label = b.slots.title || RESOURCE_LABELS[resType] || b.slots.resource_type;
    const numPersons = b.num_persons ?? 1;
    const price = formatBookingPrice(b.booked_price_cents);

    return (
      <div key={b.id} className="booking-card">
        <span className="booking-weekday">{weekday}</span>
        <span className="booking-date">{dateStr}</span>
        <span className="booking-time-start">{timeStart}</span>
        <span className="booking-time-dash">–</span>
        <span className="booking-time-end">{timeEnd}</span>
        <span className="booking-kind">{KIND_LABELS[resType] ?? resType}</span>
        <span className="booking-type">
          {label}
          <DiscountNote code={b.discount_code} offCents={b.slots.price_cents * numPersons - b.booked_price_cents} />
        </span>
        <span className="booking-persons">{numPersons > 1 ? `${numPersons} os.` : ""}</span>
        <span className="booking-price">{price} €</span>
      </div>
    );
  };

  const renderCreditTx = (tx: CreditTransaction) => {
    const date = new Date(tx.created_at);
    const weekday = date.toLocaleDateString("sk-SK", { weekday: "short" });
    const dateStr = `${date.getDate()}. ${date.getMonth() + 1}.`;
    const label = TRANSACTION_LABELS[tx.type] || tx.type;
    const detail = tx.booking ? describeBooking(tx.booking) : describeFromText(tx);
    const positive = tx.amount_cents >= 0;
    return (
      <div key={tx.id} className="booking-card booking-card-credit">
        <span className="booking-weekday">{weekday}</span>
        <span className="booking-date">{dateStr}</span>
        <span className="booking-type">
          <strong>{label}</strong>
          {detail && <span className="booking-hours">{detail}</span>}
        </span>
        <span className={`booking-price credit-amount ${positive ? "positive" : "negative"}`}>
          {positive ? "+" : ""}{(tx.amount_cents / 100).toFixed(2).replace(".", ",")} €
        </span>
      </div>
    );
  };

  return (
    <div className="my-bookings">
      <h2>Moje rezervácie</h2>
      {customer && (
        <div className="profile-box">
          <div className="profile-name">{customer.name}</div>
          <div className="profile-email">{customer.email}</div>
          <div className="profile-code">Členské číslo: <strong>{customer.customer_code}</strong></div>
          {creditBalance > 0 && (
            <div className="profile-credit">Kredit: <strong>{(creditBalance / 100).toFixed(2).replace(".", ",")} €</strong></div>
          )}
        </div>
      )}

      <div className="sub-tab-nav">
        <button
          className={`sub-tab-btn ${subTab === "reservations" ? "active" : ""}`}
          onClick={() => onSubTabChange("reservations")}
        >
          Rezervácie
        </button>
        <button
          className={`sub-tab-btn ${subTab === "credit" ? "active" : ""}`}
          onClick={() => onSubTabChange("credit")}
        >
          Kredit
        </button>
      </div>

      {subTab === "reservations" && (
        <>
          <h3>Nadchádzajúce</h3>
          {upcomingGroups.length === 0 ? (
            <p className="empty">Žiadne nadchádzajúce rezervácie.</p>
          ) : (
            <div className="booking-list booking-table">{upcomingGroups.map(renderGroupCard)}</div>
          )}

          <h3>Historické</h3>
          {pastIndividual.length === 0 ? (
            <p className="empty">Žiadne historické rezervácie.</p>
          ) : (
            <div className="booking-list booking-table">{pastIndividual.map(renderBooking)}</div>
          )}
        </>
      )}

      {subTab === "credit" && (
        <div className="credit-history">
          <h3 className="credit-balance-heading">
            <span>Zostatok</span>
            <strong>{(creditBalance / 100).toFixed(2).replace(".", ",")} €</strong>
          </h3>
          <p className="credit-note">
            Len pohyby kreditu. Platby kartou nájdete v potvrdzovacom emaile.{" "}
            Máte darčekový poukaz? <button type="button" className="link-btn" onClick={onOpenVoucher}>Uplatniť kód</button>
          </p>
          {creditTransactions.length === 0 ? (
            <p className="empty">Žiadna kreditová história.</p>
          ) : (
            <div className="booking-list booking-table">{creditTransactions.map(renderCreditTx)}</div>
          )}
        </div>
      )}
    </div>
  );
}
